/**
 * Desktop→mobile handoff: renders the short-lived `handoff.url` as a scannable
 * QR so the applicant can continue on a phone (where the camera lives). The URL
 * embeds a short-TTL handoff token — never the long-lived session bearer — so a
 * shoulder-surfed QR expires quickly.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode';

import { CopyButton } from './CopyButton';

interface QrHandoffProps {
  /** Short-lived desktop→mobile handoff URL encoded in the QR. */
  url: string;
  /**
   * Long-lived collection session URL (this origin's `/verify?token=…`) so the
   * applicant can copy a link to resume the flow later / on another device.
   * Absent on the mobile `/resume` path, where the only token in hand is the
   * short-lived handoff token — never offer it as a "collection" link there.
   */
  sessionUrl?: string;
  /**
   * Opt out of the phone handoff and run capture right here, in THIS tab, on the
   * session token already in hand. Required, not optional: this screen is a hard
   * gate in front of every desktop capture step, so a caller that renders it
   * without an escape hatch strands the applicant. Making the prop mandatory
   * turns that into a compile error instead of a support ticket.
   *
   * `null` is the one deliberate exception: the workflow's operator switched
   * desktop access off, so the phone is the only way through.
   */
  onContinueHere: (() => void) | null;
}

export function QrHandoff({ url, sessionUrl, onContinueHere }: QrHandoffProps) {
  const { t } = useTranslation();
  const [dataUrl, setDataUrl] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    QRCode.toDataURL(url, {
      margin: 2,
      width: 240,
      color: { dark: '#17142d', light: '#ffffff' },
      errorCorrectionLevel: 'M',
    })
      .then((encoded) => {
        if (active) setDataUrl(encoded);
      })
      .catch(() => {
        if (active) setDataUrl(null);
      });
    return () => {
      active = false;
    };
  }, [url]);

  return (
    <div
      className="qr-panel"
      role="region"
      aria-label={t('flow.handoff.title', { defaultValue: 'Continue on your phone' })}
      data-testid="qr-handoff"
    >
      <div className="qr-icon" aria-hidden="true">
        <PhoneIcon />
      </div>

      <h1 className="qr-title">
        {t('flow.handoff.title', { defaultValue: 'Continue on your phone' })}
      </h1>
      <p className="qr-instruction">
        {t('flow.handoff.instruction', {
          defaultValue: 'Scan this code with your phone to continue there.',
        })}
      </p>

      <div className="qr-frame">
        <span className="qr-corner qr-corner--tl" aria-hidden="true" />
        <span className="qr-corner qr-corner--tr" aria-hidden="true" />
        <span className="qr-corner qr-corner--bl" aria-hidden="true" />
        <span className="qr-corner qr-corner--br" aria-hidden="true" />
        {dataUrl ? (
          <img
            className="qr-image"
            src={dataUrl}
            alt={t('flow.handoff.alt', { defaultValue: 'Handoff QR code' })}
            width={240}
            height={240}
          />
        ) : (
          <div className="qr-skeleton" aria-hidden="true" />
        )}
      </div>

      <div className="qr-waiting" role="status">
        <span className="qr-waiting-dot" aria-hidden="true" />
        {t('flow.handoff.waiting', {
          defaultValue: 'Waiting for you to scan…',
        })}
      </div>

      <ol className="qr-steps">
        <li>{t('flow.handoff.step1', { defaultValue: 'Open the camera on your phone' })}</li>
        <li>{t('flow.handoff.step2', { defaultValue: 'Point it at this QR code' })}</li>
        <li>{t('flow.handoff.step3', { defaultValue: 'Continue verification on your phone' })}</li>
      </ol>

      {/* Escape hatch. The QR stays the hero because a phone's rear camera
          produces a materially sharper ID photo than a laptop webcam (which
          feeds OCR and the face-match), but a desktop applicant with no phone
          to hand must still be able to finish. Runs in THIS tab on the session
          token already loaded — no new tab, no short-TTL handoff token. */}
      {onContinueHere ? (
        <div className="qr-alt">
          <span className="qr-or">{t('flow.handoff.or', { defaultValue: 'or' })}</span>
          <button
            type="button"
            className="btn btn--ghost qr-continue"
            onClick={onContinueHere}
            data-testid="continue-here"
            data-track="continue_on_this_device"
          >
            {t('flow.handoff.continueHere', { defaultValue: 'Continue on this device' })}
          </button>
          <p className="qr-alt-hint">
            {t('flow.handoff.continueHereHint', {
              defaultValue: 'Your camera will open here. A phone usually takes a clearer photo of your ID.',
            })}
          </p>
        </div>
      ) : null}

      <div className="qr-footer">
        <CopyButton
          className="qr-copy"
          value={url}
          idleLabel={t('flow.handoff.copy', { defaultValue: 'Copy link' })}
          copiedLabel={t('flow.handoff.copied', { defaultValue: 'Link copied' })}
          onError={() => window.open(url, '_blank', 'noopener,noreferrer')}
        />
        {sessionUrl ? (
          <CopyButton
            className="qr-copy"
            value={sessionUrl}
            idleLabel={t('flow.handoff.copyCollection', { defaultValue: 'Copy collection link' })}
            copiedLabel={t('flow.handoff.copiedCollection', {
              defaultValue: 'Collection link copied',
            })}
          />
        ) : null}
      </div>
    </div>
  );
}

function PhoneIcon() {
  return (
    <svg viewBox="0 0 24 24" width="28" height="28" fill="none" aria-hidden="true">
      <rect
        x="7"
        y="2.5"
        width="10"
        height="19"
        rx="2.5"
        stroke="currentColor"
        strokeWidth="1.75"
      />
      <path d="M10 18.5h4" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
      <circle cx="12" cy="5.5" r="0.8" fill="currentColor" />
    </svg>
  );
}
